/**
 * RFC 0345 A3: who answers a member's message is one setting on the
 * session, kept by the owner (Anyone here, or Only I can), not a card per
 * message. The member sees which it is, and can bring their own agent for
 * work that is theirs alone. The owner sees nothing here.
 */

import type {
  GuestCard,
  GuestChoice,
} from '../../services/sharing/guestReplies'

export function GuestReplies({
  waiting,
  guest,
  onGuest,
  canAddAgent = false,
  nameOf,
  error,
}: {
  waiting?: string | undefined
  /** The member's own line, when someone else's agent could answer them. */
  guest?: GuestCard | undefined
  onGuest?: (choice: GuestChoice) => void
  /** Whether this window can start the member's own agent. */
  canAddAgent?: boolean
  /** A display name for a person id, when the window knows one. */
  nameOf?: (person: string) => string | undefined
  error?: string
}) {
  if (!waiting) return null
  const owner = guest ? nameOf?.(guest.owner) : undefined
  const addMine = canAddAgent ? (
    <Choice onClick={() => onGuest?.('add')}>Add my agent</Choice>
  ) : null
  return (
    <section
      className="mx-auto w-full max-w-3xl space-y-2 px-3 pb-2"
      aria-label="Who answers your message"
    >
      <div className="flex flex-wrap items-center gap-1.5 text-muted-foreground text-xs">
        {guest?.adding ? (
          <span className="italic">
            Your agent joins once Mod is open on your Mac.
          </span>
        ) : guest?.anyone ? (
          <>
            <span className="italic">The session’s agent is answering.</span>
            {addMine}
          </>
        ) : guest ? (
          <>
            <span className="italic">
              {`Only ${owner ?? 'the owner'} can ask the agent here. Your message is in the thread.`}
            </span>
            {addMine}
          </>
        ) : (
          <span className="italic">Your message is waiting for a reply.</span>
        )}
      </div>
      {error ? <p className="text-destructive text-xs">{error}</p> : null}
    </section>
  )
}

function Choice({
  onClick,
  children,
}: {
  onClick: () => void
  children: string
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="rounded-full border border-border px-2.5 py-0.5 text-xs hover:bg-muted"
    >
      {children}
    </button>
  )
}
